import { useEffect, useState } from 'react';
import { retryAfterSeconds } from '@/api/retryAfter';

export interface RetryCountdown {
  /** The error's Retry-After in seconds (429 / 503 only), or `null` when it carries none. */
  delay: number | null;
  /** Seconds left; 0 once Retry may be pressed (always 0 without a delay). */
  remaining: number;
}

/**
 * The Retry-After countdown behind `ErrorState` (TMM-B3) and the wizard frames that hold their
 * Retry back (TMMAPP-0081): one tick per second, the timer cleared on unmount, and a different
 * error (a failed retry) restarts it — a re-render with the same error does not.
 */
export function useRetryCountdown(error: unknown): RetryCountdown {
  const delay = retryAfterSeconds(error);
  // Reset during render, keyed on the error's identity.
  const [trackedError, setTrackedError] = useState(error);
  const [remaining, setRemaining] = useState(delay ?? 0);
  if (trackedError !== error) {
    setTrackedError(error);
    setRemaining(delay ?? 0);
  }
  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setTimeout(() => setRemaining((left) => Math.max(0, left - 1)), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);
  return { delay, remaining };
}
